import { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import site from "../data/site";
import SectionHeading from "./SectionHeading";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const emptyForm = {
  company_name: "",
  contact_person: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

function Contact() {
  const formRef = useRef(null);
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus(null);
    setSending(true);
    emailjs
      .sendForm(SERVICE_ID, TEMPLATE_ID, formRef.current, {
        publicKey: PUBLIC_KEY,
      })
      .then(() => {
        setStatus("success");
        formRef.current.reset();
        setForm(emptyForm);
      })
      .catch((err) => {
        console.error("EmailJS error:", err);
        setStatus("error");
      })
      .finally(() => setSending(false));
  };

  return (
    <section className="section section--alt" id="contact">
      <div className="container container--narrow">
        <SectionHeading
          eyebrow="Get In Touch"
          title="Let's Build Something Great"
          subtitle="Tell us about your project and our team will get back to you within one business day."
        />
        <div className="contact-card reveal">
          {status === "success" && (
            <div className="contact-card__success" role="status">
              <i
                className="bi bi-check-circle-fill contact-alert__icon"
                aria-hidden="true"
              />
              <span>
                Thank you! Your message has been sent — we will be in touch
                soon.
              </span>
              <button
                type="button"
                className="contact-alert__close"
                aria-label="Dismiss"
                onClick={() => setStatus(null)}
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>
          )}
          {status === "error" && (
            <div className="contact-card__error" role="alert">
              <i
                className="bi bi-exclamation-triangle-fill contact-alert__icon"
                aria-hidden="true"
              />
              <span>
                Sorry, something went wrong sending your message. Please email
                us directly at{" "}
                <a href={`mailto:${site.email}`}>{site.email}</a>.
              </span>
              <button
                type="button"
                className="contact-alert__close"
                aria-label="Dismiss"
                onClick={() => setStatus(null)}
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>
          )}
          <form className="contact-form" ref={formRef} onSubmit={handleSubmit}>
            <div className="contact-form__row">
              <div className="contact-form__field">
                <label htmlFor="company_name">Company Name</label>
                <input
                  id="company_name"
                  name="company_name"
                  type="text"
                  placeholder="Acme Ltd."
                  value={form.company_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="contact-form__field">
                <label htmlFor="contact_person">Point of Contact Name</label>
                <input
                  id="contact_person"
                  name="contact_person"
                  type="text"
                  placeholder="Jane Doe"
                  value={form.contact_person}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="contact-form__row">
              <div className="contact-form__field">
                <label htmlFor="email">Email Address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="jane@company.com"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="contact-form__field">
                <label htmlFor="phone">Phone Number</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+92 300 1234567"
                  value={form.phone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="contact-form__field">
              <label htmlFor="subject">Subject</label>
              <input
                id="subject"
                name="subject"
                type="text"
                placeholder="Project inquiry, WhatsApp API, website development..."
                value={form.subject}
                onChange={handleChange}
                required
              />
            </div>
            <div className="contact-form__field">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                rows="5"
                placeholder="Tell us about your project, timeline and goals..."
                value={form.message}
                onChange={handleChange}
                required
              />
            </div>
            <button
              type="submit"
              className="btn btn--primary btn--block"
              disabled={sending}
            >
              {sending ? (
                <>
                  <i className="bi bi-hourglass-split" />
                  Sending...
                </>
              ) : (
                <>
                  Send Message
                  <i className="bi bi-send" />
                </>
              )}
            </button>
            <p className="contact-form__hint">
              Or email us directly at{" "}
              <a href={`mailto:${site.email}`}>{site.email}</a>
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

export default Contact;
